'use client';

import { ElementType, ReactNode, useMemo } from 'react';

import { Avatar } from '@nextui-org/react';
import { motion } from 'framer-motion';
import {
  Home,
  Images,
  ShoppingCartIcon,
  CircleUserRound,
} from 'lucide-react';

import {
  useGetCartCountQuery,
  useGetLocalStorageCartCountQuery,
} from '@/entities/cart/query';
import { usePathname, Link } from '@/shared/navigation';
import { ROUTES } from '@/shared/routes/routes';
import { cn } from '@/shared/utils/utils';
import { useDisplayProfile } from '@/shared/hooks/use-display-profile';
import { externalAvatarImgProps } from '@/shared/const/telegram-account';

/**
 * Активный пункт: тёмная охра вместо брендового #fdc64d — жёлтый на белом
 * давал контраст ~1.6:1 (WCAG AA требует ≥ 4.5:1, аудит Lighthouse).
 * Брендовый жёлтый остаётся в подложке-«пилюле» и бейдже корзины.
 */
const ACTIVE_COLOR = '#a16207';
const INACTIVE_COLOR = '#000000';

/** Пружинка для «переезжающей» подложки и bounce иконок — как в Telegram */
const PILL_SPRING = { type: 'spring', stiffness: 500, damping: 32 } as const;
const ICON_SPRING = { type: 'spring', stiffness: 600, damping: 18 } as const;

/** С Next.js `trailingSlash: true` в pathname всегда хвостовой `/` — приводим к виду ROUTES */
const normalizePath = (p: string) => {
  const t = p.replace(/\/$/, '');
  return t === '' ? '/' : t;
};

/** Какой пункт нижнего таба считать активным (вложенные маршруты тоже) */
const getMobileTabActiveKey = (pathname: string): string => {
  const p = normalizePath(pathname);
  if (p === '/') {
    return ROUTES.home;
  }
  if (p === ROUTES.masterclasses || p.startsWith(`${ROUTES.masterclasses}/`)) {
    return ROUTES.masterclasses;
  }
  if (p === ROUTES.cart || p.startsWith(`${ROUTES.cart}/`)) {
    return ROUTES.cart;
  }
  if (p === ROUTES.me || p.startsWith(`${ROUTES.me}/`)) {
    return ROUTES.me;
  }
  return p;
};

type MobileTab = {
  key: string;
  title: string;
  icon?: ElementType;
  element?: ReactNode;
  badge?: number;
};

export const MobileMenu = () => {
  const { data: cartCount } = useGetCartCountQuery();
  const { data: localStorageCartCount } = useGetLocalStorageCartCountQuery();
  const { image: profileImage, showProfileTabAvatar } = useDisplayProfile();
  const pathname = usePathname();
  const activeTabKey = useMemo(
    () => getMobileTabActiveKey(pathname),
    [pathname],
  );
  const tabs: MobileTab[] = useMemo(() => {
    return [
      {
        key: ROUTES.home,
        title: 'Главная',
        icon: Home,
      },
      {
        key: ROUTES.masterclasses,
        title: 'Мастер-классы',
        icon: Images,
      },
      {
        key: ROUTES.cart,
        title: 'Корзина',
        icon: ShoppingCartIcon,
        badge: cartCount || localStorageCartCount || undefined,
      },
      {
        key: ROUTES.me,
        title: 'Профиль',
        element: (
          <Avatar
            className="h-10 w-10"
            isBordered
            color="default"
            src={profileImage ?? ''}
            size="sm"
            imgProps={externalAvatarImgProps}
          />
        ),
        icon: CircleUserRound,
      },
    ];
  }, [cartCount, profileImage, localStorageCartCount]);

  return (
    <div
      className={cn(
        'md:hidden z-30 mobile-menu justify-between bg-white shadow-[0_4px_15px_0_rgba(0,0,0,0.25)] rounded-3xl border-none flex overflow-hidden flex-col fixed left-3 bottom-3 right-3 transition-all duration-300',
      )}
    >
      <div id="portal-mobile-top" className="min-h-0 shrink-0 empty:hidden" />
      <nav className="flex shrink-0 items-stretch justify-between px-1 py-2">
        {tabs.map((item) => {
          const isActive = item.key === activeTabKey;
          const isProfileAvatar =
            item.key === ROUTES.me && showProfileTabAvatar;

          return (
            <Link
              prefetch
              key={item.key}
              href={item.key}
              aria-current={isActive ? 'page' : undefined}
              className="relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-1"
            >
              {/* «Переезжающая» подложка активного таба — как в Telegram */}
              {isActive && (
                <motion.span
                  layoutId="mobile-tab-pill"
                  transition={PILL_SPRING}
                  className="absolute inset-x-0 -top-1 -bottom-1 rounded-3xl bg-[#fdc64d]/15"
                />
              )}

              <motion.span
                className="relative flex h-7 items-center justify-center"
                animate={{ scale: isActive ? 1.12 : 1, y: isActive ? -1 : 0 }}
                transition={ICON_SPRING}
                whileTap={{ scale: 0.88 }}
              >
                {isProfileAvatar ? (
                  item.element
                ) : item.icon ? (
                  <item.icon
                    size={24}
                    strokeWidth={1.25}
                    style={{ color: isActive ? ACTIVE_COLOR : INACTIVE_COLOR }}
                  />
                ) : null}

                {/* Бейдж количества (корзина) */}
                {item.badge ? (
                  // text-gray-900 на жёлтом: белый текст не читался (контраст ~1.6:1)
                  <span className="absolute -right-2 -top-1 flex min-w-4 items-center justify-center rounded-full bg-[#fdc64d] px-1 text-[10px] font-semibold leading-4 text-gray-900">
                    {item.badge}
                  </span>
                ) : null}
              </motion.span>

              {!isProfileAvatar && (
                <span
                  className="relative max-w-full whitespace-nowrap text-[10px] leading-none tracking-tight transition-colors duration-200"
                  style={{ color: isActive ? ACTIVE_COLOR : INACTIVE_COLOR }}
                >
                  {item.title}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};
